import _ from 'lodash';
import { Item } from 'services/items';

import {
  AutoPurchaseOrderItem,
  createPurchaseOrder,
  getPurchaseOrderId,
  initialPurchaseOrder,
  initialPurchaseOrderItem,
  PurchaseOrder,
} from 'services/purchaseOrders';
import {
  SalesOrderBundleItem,
  SalesOrderItem,
  SalesOrderItemTypes,
} from 'services/salesOrders';
import { fetchVendorById, Vendor } from 'services/vendors';

export const resolveQuantity = (
  itemId: number | null,
  soItems: SalesOrderItem[]
) => {
  if (itemId) {
    return soItems
      .filter((soi) => soi.saleItem && soi.saleItem.itemId === itemId)
      .reduce((a, b) => {
        return a + (b.quantity || 0);
      }, 0);
  }
  return 0;
};

export const resolveItems = (
  salesItems: (SalesOrderItem | SalesOrderBundleItem)[]
) => {
  const mergedItems = salesItems
    .map((i) => {
      if (
        i.salesOrderItemType === SalesOrderItemTypes.Bundle ||
        i.salesOrderItemType === SalesOrderItemTypes.BundleCreditReturn
      ) {
        return (i as SalesOrderBundleItem).salesOrderItems.map((soi) => ({
          ...soi,
          quantity:
            i.quantity && soi.quantity
              ? i.quantity * soi.quantity
              : soi.quantity,
        }));
      }
      return i;
    })
    .flat();
  return mergedItems;
};

export const createAutoPurchaseOrders = async (
  autoItems: AutoPurchaseOrderItem[],
  locationId: number,
  vendors: Vendor[]
) => {
  const grouped = _.groupBy(autoItems, 'vendorId');
  const newOrders: PurchaseOrder[] = [];

  const getVendorById = async (id: number) => {
    try {
      const vendorFromApi = await fetchVendorById(id);
      return vendorFromApi;
    } catch (e) {
      return null;
    }
  };

  for (const items of Object.values(grouped)) {
    const item = items[0];

    const vendorFromState = vendors.find((v) => v.id === item.vendorId);

    const vendor =
      vendorFromState ||
      (item.vendorId && (await getVendorById(item?.vendorId)));

    if (!vendor) {
      continue;
    }

    const nextPoNumber = await getPurchaseOrderId();
    const purchaseOrderItemList = items.map((i) => ({
      ...initialPurchaseOrderItem,
      item: i.item,
      itemId: i.item.id,
      unitCost: i.item.cost,
      quantity: i.quantity,
      uomId: i.uomId,
    }));

    newOrders.push({
      ...initialPurchaseOrder,
      locationId: locationId,
      vendorId: vendor.id,
      vendor: vendor,
      number: nextPoNumber.toString(),
      purchaseOrderItemList,
    });
  }

  // set line numbers in po items
  const orderedPurchaseOrders = newOrders.map((o) => ({
    ...o,
    purchaseOrderItemList: o.purchaseOrderItemList.map((item, index) => ({
      ...item,
      lineNumber: index,
    })),
  }));

  return orderedPurchaseOrders;
};

export const createPurchaseOrders = async (purchaseOrders: PurchaseOrder[]) => {
  for (const po of purchaseOrders) {
    await createPurchaseOrder(po);
  }
};

export const extractVendorId = (item: Item) => {
  const vendorItems = _.get(item, 'vendorItems', []);
  const defaultVendorItem = vendorItems.find((vi) => vi?.defaultFlag);
  return (
    defaultVendorItem?.vendorId ?? _.get(vendorItems, '[0].vendorId', null)
  );
};
